import { useLanguage } from "@/contexts/LanguageContext";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const PricingTeaser = () => {
  const { t, language } = useLanguage();
  const navigate = useLocaleNavigate();

  const tiers = [
    {
      name: t.pricingTeaser.free.name,
      price: "€0",
      desc: t.pricingTeaser.free.desc,
      cta: t.pricingTeaser.free.cta,
      onClick: () => navigate("/auth"),
      variant: "outline" as const,
    },
    {
      name: t.pricingTeaser.report.name,
      price: "€5",
      suffix: t.pricingTeaser.report.suffix,
      desc: t.pricingTeaser.report.desc,
      cta: t.pricingTeaser.report.cta,
      onClick: () => navigate("/properties"),
      variant: "default" as const,
      highlighted: true,
    },
    {
      name: "Pro",
      price: "€59",
      suffix: t.pricingTeaser.pro.suffix,
      desc: t.pricingTeaser.pro.desc,
      cta: t.pricingTeaser.pro.cta,
      onClick: () => window.open(language === 'ru' ? 'https://tally.so/r/jaMBRE' : 'https://tally.so/r/zxz1Jg', '_blank'),
      variant: "outline" as const,
    },
  ];

  return (
    <section className="py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t.pricingTeaser.title}
          </h2>
          <p className="text-muted-foreground">{t.pricingTeaser.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-8">
          {tiers.map((tier, i) => (
            <div
              key={i}
              className={`rounded-2xl border p-6 text-center flex flex-col ${
                tier.highlighted
                  ? "border-primary bg-background shadow-lg ring-1 ring-primary/20"
                  : "border-border bg-background"
              }`}
            >
              <span className="text-sm font-medium text-muted-foreground mb-1">{tier.name}</span>
              <div className="mb-1">
                <span className="text-3xl font-bold text-foreground">{tier.price}</span>
                {tier.suffix && (
                  <span className="text-sm text-muted-foreground ml-1">{tier.suffix}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-5 flex-1">{tier.desc}</p>
              <Button
                variant={tier.variant}
                className="w-full rounded-full gap-1.5"
                onClick={tier.onClick}
              >
                {tier.cta}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{t.pricingTeaser.enterprise}</span>
          {" "}
          <button
            onClick={() => navigate("/contact")}
            className="text-primary hover:underline font-medium"
          >
            {t.pricingTeaser.enterpriseLink}
          </button>
        </p>
      </div>
    </section>
  );
};

export default PricingTeaser;
